import "./ClientContactInformationComponent.scss";
import * as Yup from "yup";
import {IClientContactDetails} from "../../../../shared/models/client.model";
import {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {ImageConfig, Misc} from "../../../../constants";
import FormikInputComponent from "../../../../shared/components/form-controls/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import ToolTipComponent from "../../../../shared/components/tool-tip/ToolTipComponent";
import IconButtonComponent from "../../../../shared/components/icon-button/IconButtonComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import FormikPhoneInputComponent from "../../../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import FormDebuggerComponent from "../../../../shared/components/form-debugger/FormDebuggerComponent";
import {setFormChangesData} from "../../../../store/actions/form-state.action";

interface ClientContactInformationComponentProps {
    onNext: () => void;
    appointmentId: any
}

const contactInfoSchema = Yup.object({
    phone_type: Yup.string().required('Phone type is required'),
    phone: Yup.string().required('Phone number is required'),
});

const primaryEmailInfoSchema = Yup.object({
    email: Yup.string().email('Enter a valid email address')
        .max(100, 'Email cannot be more than 100 characters')
        .required('Email address is required'),
});

const secondaryEmailInfoSchema = Yup.object({
    email: Yup.string().email('Enter a valid email address')
        .max(100, 'Email cannot be more than 100 characters').nullable(),
});

const ClientContactDetailsFormValidationSchema = Yup.object({
    primary_email: Yup.string().email('Enter a valid email address')
        .max(100, 'Email cannot be more than 100 characters')
        .required('Primary email address is required'),
    primary_contact_info: contactInfoSchema,
    // secondary_contact_info: Yup.array(secondaryContactInfoSchema).nullable(),
    secondary_emails: Yup.array(secondaryEmailInfoSchema).nullable()
});

const EmailObj = {
    email: ""
}

const PhoneObj = {
    phone_type: "",
    phone: ""
}

const ClientContactDetailsFormInitialValues: IClientContactDetails = {
    primary_email: "",
    primary_contact_info: PhoneObj,
    appointment_id: ""
}

const ClientContactInformationComponent = (props: ClientContactInformationComponentProps) => {

    const {onNext, appointmentId} = props;
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);

    const [clientContactDetailsFormInitialValues, setClientContactDetailsFormInitialValues] = useState<IClientContactDetails>(_.cloneDeep(ClientContactDetailsFormInitialValues));
    const {
        phoneTypeList,
    } = useSelector((state: IRootReducerState) => state.staticData);
    const dispatch = useDispatch();

    const {
        clientBasicDetails,
        isClientBasicDetailsLoaded,
    } = useSelector((state: IRootReducerState) => state.client);

    useEffect(() => {
        if (isClientBasicDetailsLoaded) {
            const clientBasicDetailsCopy = _.cloneDeep(clientBasicDetails);
            const clientContactDetails = {
                primary_email: clientBasicDetailsCopy?.primary_email,
                secondary_emails: clientBasicDetailsCopy?.secondary_emails?.some((item: any) => item.email !== "") ? clientBasicDetailsCopy?.secondary_emails : null,
                primary_contact_info: clientBasicDetailsCopy?.primary_contact_info,
                secondary_contact_info: clientBasicDetailsCopy?.secondary_contact_info?.some((item: any) => item.phone !== "" || item.phone_type !== "") ? clientBasicDetailsCopy?.secondary_contact_info : null,
            };
            if (!clientContactDetails?.primary_email) {
                clientContactDetails.primary_email = "";
            }
            if (!clientContactDetails?.primary_contact_info) {
                clientContactDetails.primary_contact_info = PhoneObj;
            }
            setClientContactDetailsFormInitialValues(clientContactDetails);
        }
    }, [clientBasicDetails, isClientBasicDetailsLoaded]);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = CommonService.removeKeysFromJSON(_.cloneDeep(values), ["phone_type_details"]);
        payload.appointment_id = appointmentId ? appointmentId : currentUser?.appointment_id;
        CommonService._client.ClientBasicDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setSubmitting(false);
                onNext();
                dispatch(setFormChangesData(false));
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [onNext, dispatch, currentUser, appointmentId]);

    return (
        <div className={'client-contact-information-component'}>
            <Formik
                validationSchema={ClientContactDetailsFormValidationSchema}
                initialValues={clientContactDetailsFormInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onSubmit}
            >
                {({values, setFieldValue, errors, validateForm, isValid, isSubmitting, dirty}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                        if (dirty) {
                            dispatch(setFormChangesData(true));
                        }
                    }, [validateForm, values, dirty]);
                    return (
                        <Form className="t-form" noValidate={true}>
                            <FormDebuggerComponent values={values} errors={errors} showDebugger={false}/>
                            <div className="t-form-controls with-icons">
                                <div className={'form-header'}>
                                    Primary Phone
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col">
                                        <Field name={'primary_contact_info.phone_type'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikSelectDropdownComponent
                                                        id={'phone_type_dd'}
                                                        options={phoneTypeList}
                                                        label={'Phone Type'}
                                                        placeholder={'Select Phone Type'}
                                                        required={true}
                                                        isClearable={false}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col">
                                        <Field name={'primary_contact_info.phone'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikPhoneInputComponent
                                                        label={'Phone Number'}
                                                        disabled={!values.primary_contact_info.phone_type}
                                                        placeholder={'Enter Phone Number'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col-md-1 registration-form-icons-wrapper">
                                        <IconButtonComponent className={"form-helper-icon"}>
                                            <ToolTipComponent
                                                isFormTooltip={true}
                                                showArrow={true}
                                                position={"left"}
                                                tooltip={<div className={'justified-text'}>This phone number will be
                                                    used to contact you if
                                                    necessary. Please ensure it remains active and operational at all
                                                    times.</div>}>
                                                <ImageConfig.InfoIcon/>
                                            </ToolTipComponent>
                                        </IconButtonComponent>
                                    </div>
                                </div>
                                {
                                    values?.secondary_contact_info && values?.secondary_contact_info?.length > 0 ?
                                        <>
                                            <div className={'form-header'}>
                                                Alternate Phone
                                            </div>
                                            <FieldArray
                                                name="secondary_contact_info"
                                                render={(arrayHelpers) => (
                                                    <>
                                                        {values?.secondary_contact_info && values?.secondary_contact_info?.map((item: any, index: any) => {
                                                            return (
                                                                <div className="ts-row d-flex" key={index}>
                                                                    <div className="ts-col">
                                                                        <Field
                                                                            name={`secondary_contact_info[${index}].phone_type`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikSelectDropdownComponent
                                                                                        options={phoneTypeList}
                                                                                        label={'Phone Type'}
                                                                                        required={false}
                                                                                        isClearable={false}
                                                                                        placeholder={'Select Phone Type'}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div className="ts-col">
                                                                        <Field
                                                                            name={`secondary_contact_info[${index}].phone`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikPhoneInputComponent
                                                                                        label={'Phone Number'}
                                                                                        required={false}
                                                                                        disabled={values.secondary_contact_info && !values.secondary_contact_info[index].phone_type}
                                                                                        placeholder={'Enter Phone Number'}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div
                                                                        className="ts-col-md-1 registration-form-icons-wrapper">
                                                                        {(values?.secondary_contact_info && values?.secondary_contact_info?.length > 0) &&
                                                                            <IconButtonComponent
                                                                                className={"form-helper-icon"}
                                                                                onClick={() => {
                                                                                    arrayHelpers.remove(index);
                                                                                }}
                                                                            >
                                                                                <ImageConfig.DeleteIcon
                                                                                    color={"#E84142"}/>
                                                                            </IconButtonComponent>
                                                                        }
                                                                        {(values?.secondary_contact_info && index === values.secondary_contact_info.length - 1) &&
                                                                            <IconButtonComponent
                                                                                className={"form-helper-icon"}
                                                                                disabled={!contactInfoSchema.isValidSync(values.secondary_contact_info[index])}
                                                                                onClick={() => {
                                                                                    arrayHelpers.push(PhoneObj);
                                                                                }}
                                                                            >
                                                                                <ImageConfig.AddCircleIcon/>
                                                                            </IconButtonComponent>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </>
                                                )}/>
                                        </> :
                                        <ButtonComponent variant={"link"}
                                                         color={"info"}
                                                         className={'mrg-bottom-20'}
                                                         size={"small"}
                                                         prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                         onClick={() => {
                                                             setFieldValue('secondary_contact_info', [{
                                                                 phone_type: "",
                                                                 phone: ""
                                                             }]);
                                                         }}
                                        >
                                            Add Alternate Phone
                                        </ButtonComponent>

                                }
                                <div className={'form-header'}>
                                    Primary Email Address
                                </div>
                                <div className="ts-row">
                                    <div className="ts-col">
                                        <Field name={'primary_email'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        id={'email_primary_input'}
                                                        label={'Email Address'}
                                                        placeholder={'Enter Email Address'}
                                                        type={"email"}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="ts-col-md-1 registration-form-icons-wrapper">
                                        <IconButtonComponent className={"form-helper-icon"}>
                                            <ToolTipComponent
                                                isFormTooltip={true}
                                                showArrow={true}
                                                position={"right"}
                                                tooltip={<div className={'justified-text'}>This email address will be
                                                    used to contact you if necessary. Please ensure it remains active
                                                    and operational at all times.</div>}>
                                                <ImageConfig.InfoIcon/>
                                            </ToolTipComponent>
                                        </IconButtonComponent>
                                    </div>
                                    <div className="ts-col"></div>
                                </div>
                                {
                                    values?.secondary_emails && values?.secondary_emails?.length > 0 ?
                                        <>
                                            <div className={'form-header'}>
                                                Alternate Email Address
                                            </div>
                                            <FieldArray
                                                name="secondary_emails"
                                                render={(arrayHelpers) => (
                                                    <>
                                                        {values?.secondary_emails && values?.secondary_emails?.map((item: any, index: any) => {
                                                            return (
                                                                <div className="ts-row" key={index}>
                                                                    <div className="ts-col">
                                                                        <Field
                                                                            name={`secondary_emails[${index}].email`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        label={'Email Address'}
                                                                                        placeholder={'Enter Email Address'}
                                                                                        type={"email"}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div
                                                                        className="ts-col-md-1 registration-form-icons-wrapper">
                                                                        {(values?.secondary_emails && values.secondary_emails.length > 0) &&
                                                                            <IconButtonComponent
                                                                                className={"form-helper-icon"}
                                                                                onClick={() => {
                                                                                    arrayHelpers.remove(index);
                                                                                }}
                                                                            >
                                                                                <ImageConfig.DeleteIcon
                                                                                    color={"#E84142"}/>
                                                                            </IconButtonComponent>
                                                                        }
                                                                        {values?.secondary_emails && (index === values?.secondary_emails?.length - 1) &&
                                                                            <IconButtonComponent
                                                                                className={"form-helper-icon"}
                                                                                disabled={!primaryEmailInfoSchema.isValidSync(values.secondary_emails[index])}
                                                                                onClick={() => {
                                                                                    arrayHelpers.push(EmailObj);
                                                                                }}
                                                                            >
                                                                                <ImageConfig.AddCircleIcon/>
                                                                            </IconButtonComponent>
                                                                        }
                                                                    </div>
                                                                    <div className="ts-col"></div>
                                                                </div>
                                                            )
                                                        })}
                                                    </>
                                                )}/>
                                        </> :
                                        <ButtonComponent variant={"link"}
                                                         color={"info"}
                                                         size={"small"}
                                                         className={'mrg-bottom-20'}
                                                         prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                         onClick={() => {
                                                             setFieldValue('secondary_emails', [{
                                                                 email: ""
                                                             }]);
                                                         }}
                                        >
                                            Add Alternate Email
                                        </ButtonComponent>
                                }

                                <div className="t-form-actions">
                                    <ButtonComponent
                                        id={'next_btn'}
                                        type={"submit"}
                                        size={'large'}
                                        suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                        disabled={!isValid || isSubmitting}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>
                            </div>
                        </Form>
                    )
                        ;
                }}
            </Formik>
        </div>
    );


};

export default ClientContactInformationComponent;
